import React, { useEffect, useState } from "react";
import { Download, Share, X, Plus, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { setPwaContext } from "@/lib/pwa";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const APPS = {
  "control-os": {
    icon: "/icon-control-192.png",
    name: "KEWAGPage Control OS",
    desc: "Control OS auf diesem Gerät installieren und direkt auf Kunden, Websites und Systeme zugreifen.",
    cta: "Control OS installieren",
  },
  "cockpit": {
    icon: "/icon-cockpit-192.png",
    name: "KEWAGPage Website-Cockpit",
    desc: "Ihr persönliches Website-Cockpit als App installieren – schneller Zugriff auf Website, Anfragen und Services.",
    cta: "Website-Cockpit installieren",
  },
};

// Hochwertige, klar benannte Installationskarte je Bereich (Control OS bzw. Website-Cockpit).
export default function InstallAppCard({ appKey = "cockpit" }) {
  const { t } = useI18n();
  const app = APPS[appKey] || APPS.cockpit;
  const DISMISS_KEY = `kewag_pwa_card_dismissed_${appKey}`;
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setPwaContext(appKey); // passendes Manifest für diese Seite aktiv setzen
  }, [appKey]);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") { setHidden(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIOS()) setIos(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [DISMISS_KEY]);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch {}
      setDeferred(null);
      return;
    }
    setShowIos(true); // iOS oder Browser ohne Prompt → Anleitung anzeigen
  };

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };

  if (hidden) return null;

  return (
    <div data-testid={`install-card-${appKey}`}
      className="mt-6 rounded-2xl border border-[#A3E635]/25 bg-gradient-to-br from-[#A3E635]/[0.07] to-transparent p-4 sm:p-5 relative">
      <button onClick={dismiss} data-testid={`install-card-dismiss-${appKey}`} aria-label={t("Schliessen")}
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3.5">
        <img src={app.icon} alt={app.name} className="w-12 h-12 rounded-xl shrink-0 border border-white/10" draggable="false" />
        <div className="min-w-0 pr-5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#A3E635] mb-0.5">
            <Download className="w-3 h-3" /> {t("Als App installieren")}
          </div>
          <div className="font-bold text-white text-[15px] leading-tight">{app.name}</div>
          <p className="text-[13px] text-gray-400 mt-1 leading-snug">{t(app.desc)}</p>
        </div>
      </div>
      {installed ? (
        <div data-testid={`install-card-done-${appKey}`} className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#A3E635]">
          <Check className="w-4 h-4" /> {t("App installiert")}
        </div>
      ) : (
        <button onClick={install} data-testid={`install-card-btn-${appKey}`}
          className="mt-4 w-full flex items-center justify-center gap-2 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-xl transition-colors active:scale-[0.99]">
          {ios ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />} {t(app.cta)}
        </button>
      )}

      {showIos && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4" data-testid={`install-hint-${appKey}`}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowIos(false)} />
          <div className="relative w-full max-w-sm bg-[#16161c] border border-white/10 rounded-2xl p-6 text-center">
            <img src={app.icon} alt={app.name} className="w-16 h-16 rounded-2xl mx-auto mb-4 border border-white/10" />
            <h3 className="font-bold text-lg text-white">{app.name}</h3>
            <p className="text-sm text-gray-400 mt-2">
              {ios ? t("So installieren Sie diese App auf Ihrem iPhone oder iPad:")
                   : t("So installieren Sie diese App über Ihren Browser:")}
            </p>
            <ol className="text-left text-sm text-gray-300 mt-4 space-y-2.5">
              {ios ? (
                <>
                  <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">1</span> {t("Tippen Sie unten auf das Teilen-Symbol")} <Share className="w-4 h-4 text-[#A3E635]" /></li>
                  <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">2</span> {t("Wählen Sie «Zum Home-Bildschirm»")} <Plus className="w-4 h-4 text-[#A3E635]" /></li>
                  <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">3</span> {t("Bestätigen Sie mit «Hinzufügen»")}</li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">1</span> {t("Öffnen Sie das Browser-Menü (drei Punkte).")}</li>
                  <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">2</span> {t("Wählen Sie «App installieren» bzw. «Zum Startbildschirm hinzufügen».")}</li>
                </>
              )}
            </ol>
            <button onClick={() => setShowIos(false)} data-testid={`install-hint-close-${appKey}`}
              className="mt-6 w-full font-bold py-2.5 rounded-xl bg-[#A3E635] text-[#071005] active:scale-95 transition-transform">{t("Verstanden")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
